<template>
  <div>
    <div class="d-flex align-items-center justify-content-between mb-2">
      <h5 class="mb-0">{{ $t("family.dashboard.market.yourOrders") }}</h5>
      <select
        v-model="latestOrdersLimit"
        class="form-control form-control-sm"
        style="width: auto"
      >
        <option :value="5">5</option>
        <option :value="10">10</option>
        <option :value="20">20</option>
      </select>
    </div>
    <table
      v-if="latestOrders.length > 0"
      class="table table-hover border-white bg-gray-100 rounded mb-4"
    >
      <thead>
        <tr>
          <th></th>
          <th>{{ $t("family.orders.order.date_order") }}</th>
          <th>{{ $t("family.orders.order.date_delivery") }}</th>
          <th>{{ $t("family.orders.order.price_tot") }}</th>
          <th>{{ $t("family.orders.order.market") }}</th>
          <th>{{ $t("family.orders.order.family") }}</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="order in latestOrders" :key="order.id">
          <td class="border-white">
            <router-link
              :to="{
                name: 'RoleOrdersManage',
                params: { id: roleId },
                query: { view: 'ddmp' },
              }"
              >#{{ order.id }}</router-link
            >
          </td>
          <td class="border-white">{{ orderDate(order) }}</td>
          <td class="border-white">{{ orderScheduledDelivery(order) }}</td>
          <td class="border-white">{{ orderTotal(order, currencySymbol) }}</td>
          <td class="border-white">{{ orderMarketName(order) }}</td>
          <td class="border-white">
            <span
              >{{ orderFamilyName(order) }} (<span v-if="orderUser(order)">{{
                orderUser(order)
              }}</span
              >)</span
            >
          </td>
        </tr>
      </tbody>
      <tfoot>
        <tr>
          <td colspan="6" class="text-left border-white bg-white">
            <router-link
              :to="{
                name: 'RoleOrdersManage',
                params: { id: roleId },
                query: { view: 'ddmp' },
              }"
              >{{
                $t("family.dashboard.products.producerviewproducts")
              }}
              →</router-link
            >
          </td>
        </tr>
      </tfoot>
    </table>
    <table
      v-else-if="loadingOrders"
      class="table table-hover bg-gray-100 rounded border-white mb-4"
    >
      <thead>
        <tr>
          <th></th>
          <th>{{ $t("family.orders.order.date_order") }}</th>
          <th>{{ $t("family.orders.order.date_delivery") }}</th>
          <th>{{ $t("family.orders.order.price_tot") }}</th>
          <th>{{ $t("family.orders.order.market") }}</th>
          <th>{{ $t("family.orders.order.family") }}</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="n in 5" :key="n">
          <td class="py-0 px-2 border-white"><Skeleton width="100%" /></td>
          <td class="py-0 px-2 border-white"><Skeleton width="100%" /></td>
          <td class="py-0 px-2 border-white"><Skeleton width="100%" /></td>
          <td class="py-0 px-2 border-white"><Skeleton width="100%" /></td>
          <td class="py-0 px-2 border-white"><Skeleton width="100%" /></td>
          <td class="py-0 px-2 border-white"><Skeleton width="100%" /></td>
        </tr>
      </tbody>
    </table>
    <p class="text-center" v-else>
      <span v-if="loadingOrders"><CSpinner color="info" /></span>
      <span v-else>{{ $t("family.dashboard.market.noOrders") }}</span>
    </p>

    <div class="bg-gray-100 rounded p-3 mb-3">
      <h6 class="mb-2 text-dark">
        {{ $t("family.dashboard.market.exportOrders") }}
      </h6>
      <div class="d-flex align-items-center flex-wrap" style="gap: 0.5rem">
        <div>
          <label class="small mb-0 mr-1">{{
            $t("family.invoices.filters.from")
          }}</label>
          <input
            type="date"
            v-model="exportFrom"
            class="form-control form-control-sm d-inline-block"
            style="width: auto"
          />
        </div>
        <div>
          <label class="small mb-0 mr-1">{{
            $t("family.invoices.filters.to")
          }}</label>
          <input
            type="date"
            v-model="exportTo"
            class="form-control form-control-sm d-inline-block"
            style="width: auto"
          />
        </div>
        <CButton
          color="primary"
          size="sm"
          :disabled="!exportFrom || !exportTo || exportLoading"
          @click="exportOrders"
        >
          <CSpinner size="sm" v-if="exportLoading" class="mr-1" />
          {{ $t("family.dashboard.market.exportCSV") }}
        </CButton>
        <span v-if="exportError" class="text-danger small">{{
          exportError
        }}</span>
      </div>
    </div>

    <hr v-if="hasData" />

    <h5 v-if="hasData">{{ $t("family.dashboard.market.yourPerformance") }}</h5>

    <div class="d-flex justify-content-center" v-if="loading">
      <CSpinner color="info" />
    </div>
    <div v-else-if="hasData">
      <div
        style="height: 250px; position: relative"
        class="p-2 mb-4 bg-gray-100 rounded"
      >
        <CChartLine
          :datasets="defaultDatasets"
          :labels="defaultLabels"
          :options="options"
          :plugins="chartPlugins"
          style="height: 250px"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { CChartLine } from "@coreui/vue-chartjs";
import ChartDataLabels from "chartjs-plugin-datalabels";
import Skeleton from "../../common/table/Skeleton.vue";
import { apiGet, apiGetDirectUrl } from "../../helpers/apiHelper";
import moment from "moment";
import orderMixin from "../../mixins/order";
import { mapGetters } from "vuex";

export default {
  mixins: [orderMixin],
  data() {
    return {
      loading: false,
      loadingOrders: true,
      latestOrdersLimit: 5,
      exportFrom: `${new Date().getFullYear()}-01-01`,
      exportTo: new Date().toISOString().split("T")[0],
      exportLoading: false,
      exportError: "",
      chartPlugins: [ChartDataLabels],
      latestOrders: [],
      start: "",
      end: "",
      dataset: [],
      options: {
        maintainAspectRatio: false,
        layout: {
          padding: {
            top: 35,
            left: 30,
            right: 50,
            bottom: 15,
          },
        },
        legend: {
          display: false,
        },
        tooltips: {
          enabled: false,
        },
        plugins: {
          datalabels: {
            display: false,
          },
        },
        scales: {
          xAxes: [
            {
              gridLines: {
                display: false,
              },
            },
          ],
          yAxes: [
            {
              gridLines: {
                display: false,
              },
              ticks: {
                display: false,
              },
            },
          ],
        },
      },
    };
  },

  mounted() {
    this.getData();
    this.getLatestOrders();
  },
  watch: {
    // whenever question changes, this function will run
    roleId(newRoleId, oldRoleId) {
      if (newRoleId != oldRoleId) {
        this.latestOrders = [];
        this.getData();
        this.getLatestOrders();
      }
    },
    latestOrdersLimit() {
      this.loadingOrders = true;
      this.getLatestOrders();
    },
  },
  methods: {
    async exportOrders() {
      this.exportLoading = true;
      this.exportError = "";

      const from = new Date(this.exportFrom);
      const to = new Date(this.exportTo);
      to.setHours(23, 59, 59, 999);

      const fromIso = from.toISOString();
      const toIso = to.toISOString();

      const filter = `(ddmi__time_limit gt '${fromIso}') and (ddmi__time_limit lt '${toIso}')`;
      const url = `/api/order/${
        this.roleId
      }/rorder/?$filter=${encodeURIComponent(filter)}&$skip=0&$top=10000`;

      const response = await apiGetDirectUrl(url);
      this.exportLoading = false;

      if (!response || response.status !== 200) {
        this.exportError = "Export failed. Please try again.";
        return;
      }

      const orders =
        response.data.payload?.Items || response.data.payload || [];
      if (orders.length === 0) {
        this.exportError = "No orders found for the selected period.";
        return;
      }

      const headers = [
        "ID",
        "Order Date",
        "Delivery Date",
        "Total (CHF)",
        "Total Original (CHF)",
        "Products",
        "Market",
        "Distributor",
        "Status",
        "Family",
        "User",
        "Email",
        "Phone",
      ];
      const rows = orders.map((order) => [
        order.id,
        moment(order.ts_created).locale("de").format("L LT"),
        this.orderScheduledDelivery(order),
        this.orderTotal(order, this.currencySymbol),
        this.orderTotalOrig(order, this.currencySymbol),
        order.view.tot_prods,
        this.orderMarketName(order),
        this.orderDeliveryName(order),
        this.orderStatus(order).label,
        this.orderFamilyName(order),
        this.orderUser(order),
        this.orderFamilyEmail(order),
        `+${this.orderFamilyPhone(order)}`,
      ]);

      const csvContent = [headers, ...rows]
        .map((row) =>
          row
            .map((cell) => `"${String(cell ?? "").replace(/"/g, '""')}"`)
            .join(";")
        )
        .join("\n");

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `orders_${this.exportFrom}_${this.exportTo}.csv`;
      link.click();
      URL.revokeObjectURL(link.href);
    },
    async getData() {
      this.loading = true;
      const response = await apiGet(
        "role_producer_dash_orders",
        [this.roleId],
        null
      );
      if (response && response.data && response.status === 200) {
        this.loading = false;
        this.dataset = response.data.data;
        this.start = moment(response.data.start).locale("de").format("L");
        this.end = moment(response.data.end).locale("de").format("L");
      }
    },
    async getLatestOrders() {
      this.loadingOrders = true;
      const response = await apiGetDirectUrl(
        `/api/order/${this.roleId}/rorder/?$top=${this.latestOrdersLimit}`
      );
      this.loadingOrders = false;
      if (response && response.data && response.status === 200) {
        console.log("Latest Orders:", response.data);
        this.latestOrders = response.data.payload;
      }
    },
  },
  components: {
    CChartLine,
    Skeleton,
  },
  computed: {
    ...mapGetters("connections", ["currencySymbol", "familyId"]),
    roleId() {
      return this.$store.state.role.id;
    },
    defaultDatasets() {
      let list_products = [];
      let list_chf = [];
      this.dataset.forEach(function (data, index) {
        // console.log(data, index);
        list_products.push(data.orders);
        list_chf.push(data.tot);
      });
      return [
        {
          label: this.$t("family.dashboard.orders.label_orders"),
          backgroundColor: "rgba(215, 91, 79, 0.75)",
          borderColor: "rgba(215, 91, 79, 0.8)",
          borderWidth: 1,
          data: list_products,
          datalabels: {
            display: false,
          },
        },
        {
          label: "CHF",
          backgroundColor: "rgba(110, 160, 110, 0.5)",
          borderColor: "rgba(110, 160, 110, 0.8)",
          borderWidth: 1,
          data: list_chf,
          datalabels: {
            display: true,
            anchor: "end",
            align: "top",
            clamp: true,
            borderRadius: 4,
            backgroundColor: "rgba(60, 60, 60, 0.8)",
            color: "#fff",
            font: { size: 10, weight: "bold" },
            padding: { top: 4, bottom: 4, left: 8, right: 8 },
            formatter: (value, ctx) => {
              const datasets = ctx.chart.data.datasets;
              const i = ctx.dataIndex;
              const products = datasets[0].data[i];
              const chf = datasets[1].data[i].toFixed(2);
              return [`🥕 ${products}`, `🪙 CHF ${chf}`];
            },
          },
        },
      ];
    },
    defaultLabels() {
      let list_labels = [];
      this.dataset.forEach(function (data, index) {
        // console.log(data, index);
        list_labels.push(data.label);
      });
      return list_labels;
    },
    hasData() {
      return this.dataset.some((data) => data.orders !== 0 || data.tot !== 0);
    },
  },
};
</script>
